import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import * as Query from '../../../api/gql/query'
import { PHOTO_ALBUMS, PHOTO_FOLDERS } from '../api'
import { bySort, folderLabel, folderLabels } from '../lib/folders'
import type { PhotoUi } from '../lib/permissions'
import type { PhotoAlbum, PhotoFolder, PhotoVisibility } from '../types'
import TagPicker from './TagPicker'

export interface BatchMetadata {
  folderId: string | null
  buildingId: string | null
  floorId: string | null
  trade: string | null
  albumId: string | null
  tagIds: string[]
  description: string
  visibility: PhotoVisibility
}

export const EMPTY_BATCH_METADATA: BatchMetadata = {
  folderId: null,
  buildingId: null,
  floorId: null,
  trade: null,
  albumId: null,
  tagIds: [],
  description: '',
  visibility: 'PROJECT',
}

export type BatchField = keyof BatchMetadata
/** Bulk mode: which fields the user ticked "change" on; only those are applied. */
export type BatchChanged = Partial<Record<BatchField, boolean>>

const NONE = '__none__'

interface Floor { id: string; name: string }
interface BuildingItem { id: string; name: string; code: string | null; floors: Floor[] }

interface Props {
  projectId: string
  value: BatchMetadata
  onChange: (value: BatchMetadata) => void
  ui: PhotoUi
  mode: 'upload' | 'bulk'
  changed?: BatchChanged
  onChangedChange?: (changed: BatchChanged) => void
  /** Fields to leave out (the bulk dialog has no description; folder and visibility have their own actions). */
  hide?: BatchField[]
}

/** Folders depth-first by sortOrder, labelled with their depth. */
export const orderedFolders = (folders: PhotoFolder[]): { folder: PhotoFolder; depth: number }[] => {
  const out: { folder: PhotoFolder; depth: number }[] = []
  const walk = (parentId: string | null, depth: number) => {
    folders
      .filter((f) => f.parentId === parentId)
      .sort(bySort)
      .forEach((f) => {
        out.push({ folder: f, depth })
        walk(f.id, depth + 1)
      })
  }
  walk(null, 0)
  return out
}

interface FieldRowProps {
  id: string
  label: string
  bulk: boolean
  checked: boolean
  onChecked: (on: boolean) => void
  children: React.ReactNode
}

const FieldRow: React.FC<FieldRowProps> = ({ id, label, bulk, checked, onChecked, children }) => {
  const { t } = useTranslation()
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label htmlFor={id}>{label}</Label>
        {bulk && (
          <div className="flex items-center gap-1.5">
            <Checkbox id={`${id}-change`} checked={checked} onCheckedChange={(on) => onChecked(on === true)} />
            <Label htmlFor={`${id}-change`} className="text-xs font-normal text-muted-foreground">{t('Photos_bulk_change')}</Label>
          </div>
        )}
      </div>
      <div className={bulk && !checked ? 'pointer-events-none opacity-50' : undefined}>{children}</div>
    </div>
  )
}

const BatchMetadataFields: React.FC<Props> = ({ projectId, value, onChange, ui, mode, changed = {}, onChangedChange, hide = [] }) => {
  const { t } = useTranslation()
  const skip = { variables: { projectId }, skip: !projectId }
  const { data: folderData } = useQuery<{ photoFolders: PhotoFolder[] }>(PHOTO_FOLDERS, skip)
  const { data: albumData } = useQuery<{ photoAlbums: PhotoAlbum[] }>(PHOTO_ALBUMS, { ...skip, skip: !projectId || !ui.canManageStructure || mode === 'bulk' })
  const { data: buildingData } = useQuery(Query.BUILDINGS, skip)
  const { data: settingsData } = useQuery(Query.SNAG_SETTINGS, skip)

  const folders = useMemo(() => orderedFolders(folderData?.photoFolders ?? []), [folderData])
  const albums = albumData?.photoAlbums ?? []
  const buildings: BuildingItem[] = buildingData?.buildings ?? []
  const trades: string[] = settingsData?.snagSettings?.infrastructureTypes ?? []
  const floors = buildings.find((b) => b.id === value.buildingId)?.floors ?? []

  const set = (patch: Partial<BatchMetadata>) => onChange({ ...value, ...patch })
  const bulk = mode === 'bulk'
  const idOf = (field: BatchField) => `photos-batch-${field}`
  const off = (field: BatchField) => bulk && !changed[field]

  const Field = (field: BatchField, label: string, children: React.ReactNode) => hide.indexOf(field) >= 0 ? null : (
    <FieldRow
      key={field}
      id={idOf(field)}
      label={label}
      bulk={bulk}
      checked={!!changed[field]}
      onChecked={(on) => onChangedChange?.({ ...changed, [field]: on })}
    >
      {children}
    </FieldRow>
  )

  const pick = (val: string | null, onPick: (v: string | null) => void, items: { id: string; label: string }[], noneLabel: string, field: BatchField) => (
    <Select value={val || NONE} onValueChange={(v) => onPick(v === NONE ? null : v)} disabled={off(field)}>
      <SelectTrigger id={idOf(field)} className="h-9"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE}>{noneLabel}</SelectItem>
        {items.map((i) => <SelectItem key={i.id} value={i.id}>{i.label}</SelectItem>)}
      </SelectContent>
    </Select>
  )

  const none = t('Common_none')
  const buildingItems = buildings.map((b) => ({ id: b.id, label: b.code ? `${b.code} - ${b.name}` : b.name }))

  return (
    <div className="space-y-4">
      {Field('folderId', t('Photos_field_folder'), (
        <Select value={value.folderId || NONE} onValueChange={(v) => set({ folderId: v === NONE ? null : v })} disabled={off('folderId')}>
          <SelectTrigger id={idOf('folderId')} className="h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            {!bulk && <SelectItem value={NONE}>{t('Photos_inbox')}</SelectItem>}
            {folders.map(({ folder, depth }) => (
              <SelectItem key={folder.id} value={folder.id}>
                {`${'– '.repeat(depth)}${folderLabel(folder, folderLabels(t))}`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ))}

      {buildings.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          {Field('buildingId', t('Photos_field_building'), pick(value.buildingId, (v) => set({ buildingId: v, floorId: null }), buildingItems, none, 'buildingId'))}
          {Field('floorId', t('Photos_field_floor'), pick(value.floorId, (v) => set({ floorId: v }), floors.map((f) => ({ id: f.id, label: f.name })), none, 'floorId'))}
        </div>
      )}

      {trades.length > 0 && Field('trade', t('Photos_filter_trade'), pick(value.trade, (v) => set({ trade: v }), trades.map((tr) => ({ id: tr, label: tr })), none, 'trade'))}

      {!bulk && ui.canManageStructure && Field('albumId', t('Photos_field_album'), pick(value.albumId, (v) => set({ albumId: v }), albums.map((a) => ({ id: a.id, label: a.name })), none, 'albumId'))}

      {Field('tagIds', t('Photos_filter_tags'), (
        <TagPicker projectId={projectId} value={value.tagIds} onChange={(tagIds) => set({ tagIds })} allowCreate={ui.canManageStructure} />
      ))}

      {Field('description', t('Photos_field_description'), (
        <Textarea id={idOf('description')} rows={2} value={value.description} disabled={off('description')} onChange={(e) => set({ description: e.target.value })} />
      ))}

      {Field('visibility', t('Photos_filter_visibility'), (
        <Select value={value.visibility} onValueChange={(v) => set({ visibility: v as PhotoVisibility })} disabled={off('visibility')}>
          <SelectTrigger id={idOf('visibility')} className="h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="PROJECT">{t('Photos_visibility_project')}</SelectItem>
            <SelectItem value="INTERNAL">{t('Photos_visibility_internal')}</SelectItem>
          </SelectContent>
        </Select>
      ))}
    </div>
  )
}

export default BatchMetadataFields
